import { Check } from "lucide-react";
import { Link } from "react-router";

import { type Plan, type Price, pricing } from "@/stripe/pricing";
import { Button } from "@/ui/button";
import { cn } from "@/utils/tailwind";

interface PricingPlansProps {
  className?: string;
}

// Present every publicly available subscription from the Stripe catalog.
//
export function PricingPlans({ className }: PricingPlansProps) {
  return (
    <div className={className}>
      <div className="grid gap-5 sm:grid-cols-2">
        {pricing.flatMap((plan) =>
          plan.prices
            .filter((price) => price.available && price.mode === "subscription")
            .map((price) => (
              <PricingCard key={price.id} plan={plan} price={price} />
            )),
        )}
      </div>

      {publicEnv.stripeTrialDays > 0 && (
        <p className="text-muted-foreground mt-6 text-center text-sm">
          Try every feature free for {publicEnv.stripeTrialDays} days. No
          payment method is required to start.
        </p>
      )}
    </div>
  );
}

interface PricingCardProps {
  plan: Plan;
  price: Price;
}

// Present one catalog price as a complete family-workspace subscription.
//
function PricingCard({ plan, price }: PricingCardProps) {
  const isFeatured = plan.name.toLowerCase().includes("annual");
  const buttonLabel =
    publicEnv.stripeTrialDays > 0 ? "Start free trial" : "Choose this plan";
  const signupSearchParams = new URLSearchParams({
    priceId: price.id,
    redirectTo: "/billing",
  });

  return (
    <article
      className={cn(
        "panel-shadow bg-card relative flex flex-col rounded-2xl border p-7",
        isFeatured && "border-primary",
      )}
    >
      {isFeatured && (
        <span className="bg-primary text-primary-foreground absolute top-0 right-6 -translate-y-1/2 rounded-full px-3 py-1 font-mono text-[11px] font-semibold tracking-wide uppercase">
          Best value
        </span>
      )}
      <p className="text-primary font-mono text-xs font-semibold tracking-[0.14em] uppercase">
        {plan.name}
      </p>
      <h3 className="font-display mt-4 text-4xl font-semibold">{price.name}</h3>
      {price.description && (
        <p className="text-muted-foreground mt-2 text-sm">
          {price.description}
        </p>
      )}

      <ul className="text-muted-foreground mt-7 grid gap-3 text-sm">
        <li className="flex items-center gap-2">
          <Check className="text-success size-4" />
          One subscription for the family workspace
        </li>
        <li className="flex items-center gap-2">
          <Check className="text-success size-4" />
          Handwriting practice, dated keepsakes, and guardian review
        </li>
        <li className="flex items-center gap-2">
          <Check className="text-success size-4" />
          Read and download saved pages after cancellation
        </li>
      </ul>

      <Button
        asChild
        className="mt-8 h-11"
        variant={isFeatured ? "default" : "outline"}
      >
        <Link to={`/login?${signupSearchParams}`}>{buttonLabel}</Link>
      </Button>
    </article>
  );
}
